import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import FeedTab from "../../components/tabs/FeedTab";

// Import images
import rebelBanner from "../../images/kalps/rebel-banner.jpg";
import rebelCard from "../../images/kalps/rebel-card.jpg";
import dreamBanner from "../../images/kalps/dream-banner.jpg";
import dreamCard from "../../images/kalps/dream-card.jpg";
import buildBanner from "../../images/kalps/build-banner.jpg";
import buildCard from "../../images/kalps/build-card.jpg";
import growBanner from "../../images/kalps/grow-banner.jpg";
import growCard from "../../images/kalps/grow-card.jpg";
import flowBanner from "../../images/kalps/flow-banner.jpg";
import flowCard from "../../images/kalps/flow-card.jpg";
import heroStory from "../../images/kalps/hero-story.jpg";


/* ─────────────────────────────────────────────────────────────────────────────
   GLASS TILE — exact from existing codebase
───────────────────────────────────────────────────────────────────────────── */
function GlassTile({ children, className = "" }) {
  return (
    <div
      className={
        "bg-white/40 backdrop-blur-md border border-white/25 rounded-2xl shadow-md p-6 " +
        className
      }
      style={{ WebkitBackdropFilter: "blur(12px) saturate(140%)" }}
    >
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   NAV — exact from existing codebase
───────────────────────────────────────────────────────────────────────────── */
function GlassNav() {
  return (
    <nav className="fixed top-4 left-0 right-0 z-50 px-6">
      <div className="max-w-7xl mx-auto flex items-center justify-between p-4 bg-white/40 backdrop-blur-md border border-white/25 rounded-full shadow-md">
        <div className="flex items-center space-x-3">
          {/* <div className="w-10 h-10 rounded-full bg-white/70 flex items-center justify-center text-xl font-bold text-blue-700">
            5
          </div>
          <div className="text-xl font-semibold text-gray-800">5BUDDIES</div> */}
        </div>

        <div className="hidden md:flex items-center space-x-6">
          <a href="/" className="text-blue-700 font-semibold">Home</a>
          <a href="/shop" className="text-gray-700 hover:text-blue-600">Shop</a>
          <a href="/cart" className="text-gray-700 hover:text-blue-600">Cart</a>
          <a href="/authlogin" className="text-gray-700 hover:text-blue-600">Login</a>
          <div className="w-9 h-9 rounded-full bg-gray-200 ring-1 ring-white/60" />
        </div>

        <button className="md:hidden p-2 rounded bg-white/30">☰</button>
      </div>
    </nav>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   KALP DATA
   Replace the src values with your actual image paths or S3 URLs.
   See the IMAGE STORAGE GUIDE comment at the bottom of this file.
───────────────────────────────────────────────────────────────────────────── */
const KALPS = [
  {
    id: "REBEL_KALP",
    name: "Rebel Kalp",
    tagline: "For those who question limits and build their own path.",
    description:
      "Rebel Kalp is where you’ll find Nomads who don’t follow the default path blindly. They think for themselves, question norms, and move with intent. These are not people trying to stand out for attention. They simply refuse to fit into something that doesn’t feel right. They build their own direction, even if it means starting alone.",
    // Hero banner image (wide landscape) — use the "REBEL KALP" banner shot
    bannerSrc: rebelBanner,
    // Portrait card image — the single red Kalp character on dark red bg
    cardSrc: rebelCard,
    accent: "#c0392b",
    gradFrom: "from-red-900",
    gradTo: "to-red-600",
    bgGlow: "bg-red-500/20",
    textAccent: "text-red-500",
    btnBg: "bg-red-700 hover:bg-red-800",
    cardBg: "from-red-950/80 to-red-800/60",
    collectionLabel: "Rebel Collection",
    collectionColor: "bg-red-900/80 text-white",
  },
  {
    id: "DREAM_KALP",
    name: "Dream Kalp",
    tagline: "For minds that imagine new worlds before they exist.",
    description:
      "Dream Kalp is where you’ll find Nomads who don’t just accept reality as it is. They imagine what doesn’t exist yet. They see patterns, possibilities, and futures others overlook. These are not people escaping reality. They are quietly shaping new ones. They don’t always have answers. But they are always asking better questions. If you feel drawn to ideas before they become real, you carry the Dream Kalp. Scroll down to explore the Dream Kalp collection.",
    bannerSrc: dreamBanner,
    cardSrc: dreamCard,
    accent: "#7c3aed",
    gradFrom: "from-purple-950",
    gradTo: "to-purple-700",
    bgGlow: "bg-purple-500/20",
    textAccent: "text-purple-400",
    btnBg: "bg-purple-700 hover:bg-purple-800",
    cardBg: "from-purple-950/80 to-purple-800/60",
    collectionLabel: "Dream Collection",
    collectionColor: "bg-purple-900/80 text-white",
  },
  {
    id: "BUILD_KALP",
    name: "Build Kalp",
    tagline: "For minds that think and build beyond limits.",
    description:
      "Build Kalp is where you’ll find Nomads who turn ideas into reality. They don’t just think or talk — they execute. They break things down, test, iterate, and keep building even when it’s messy. These are founders, engineers, operators, and creators who care more about progress than perfection. They understand that nothing meaningful appears instantly. Everything is built — step by step, decision by decision. If you feel the urge to create something real, you carry the Build Kalp.",
    bannerSrc: buildBanner,
    cardSrc: buildCard,
    accent: "#1d4ed8",
    gradFrom: "from-blue-950",
    gradTo: "to-blue-700",
    bgGlow: "bg-blue-500/20",
    textAccent: "text-blue-400",
    btnBg: "bg-blue-700 hover:bg-blue-800",
    cardBg: "from-blue-950/80 to-blue-800/60",
    collectionLabel: "Build Collection",
    collectionColor: "bg-blue-900/80 text-white",
  },
  {
    id: "GROW_KALP",
    name: "Grow Kalp",
    tagline: "For those committed to constant learning and progress.",
    description:
      "Grow Kalp is where you’ll find Nomads who focus on becoming better over time. They are not chasing quick wins or loud success. They show up consistently, learn, adapt, and evolve quietly. These are people who understand that real growth is slow, but once it compounds, it changes everything. They don’t compare constantly. They build themselves — step by step, day by day. If you believe in long-term progress over instant results, you carry the Grow Kalp ",
    bannerSrc: growBanner,
    cardSrc: growCard,
    accent: "#15803d",
    gradFrom: "from-green-950",
    gradTo: "to-green-700",
    bgGlow: "bg-green-500/20",
    textAccent: "text-green-400",
    btnBg: "bg-green-700 hover:bg-green-800",
    cardBg: "from-green-950/80 to-green-800/60",
    collectionLabel: "Grow Collection",
    collectionColor: "bg-green-900/80 text-white",
  },
  {
    id: "FLOW_KALP",
    name: "Flow Kalp",
    tagline: "For souls who move with calm focus and natural rhythm.",
    description:
      "Flow Kalp is where you’ll find Nomads who move through life with clarity and rhythm. They are not forcing things constantly. They understand when to act, when to pause, and when to let things unfold. These are people who trust their process, stay present, and move with a natural sense of direction. They don’t rush blindly. They don’t resist everything. They adapt, respond, and stay in motion. If you feel most aligned when things just “click” and move smoothly, you carry the Flow Kalp.",
    bannerSrc: flowBanner,
    cardSrc: flowCard,
    accent: "#c2410c",
    gradFrom: "from-orange-950",
    gradTo: "to-orange-600",
    bgGlow: "bg-orange-500/20",
    textAccent: "text-orange-400",
    btnBg: "bg-orange-700 hover:bg-orange-800",
    cardBg: "from-orange-950/80 to-orange-700/60",
    collectionLabel: "Flow Collection",
    collectionColor: "bg-orange-900/80 text-white",
  },
];

/* ─────────────────────────────────────────────────────────────────────────────
   KALP DETAIL PAGE — shown inline when Explore is clicked
───────────────────────────────────────────────────────────────────────────── */
function KalpDetailPage({ kalp, onBack }) {
  const categoryMap = {
    rebel: "REBEL_KALP",
    dream: "DREAM_KALP",
    build: "BUILD_KALP",
    grow: "GROW_KALP",
    flow: "FLOW_KALP",
  };
  return (
    <motion.div
      key={kalp.id}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -30 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="max-w-3xl mx-auto px-4 pb-20"
    >
      {/* Back button */}
      <button
        onClick={onBack}
        className="mt-6 mb-6 flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-blue-600 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back to all Kalps
      </button>

      {/* Name + tagline */}
      <h1 className="text-4xl font-bold text-gray-900 text-center">{kalp.name}</h1>
      <p className="text-center text-gray-600 mt-2 mb-6 text-lg">{kalp.tagline}</p>

      {/* Description card */}
      <GlassTile className="mb-6 !p-5">
        <p className="text-gray-700 leading-relaxed">
          <span className="font-bold">{kalp.name}</span> {kalp.description.replace(kalp.name, "").trimStart()}
        </p>
      </GlassTile>

      {/* Banner image */}
      <div className="rounded-2xl overflow-hidden shadow-xl mb-8">
        <img
          src={kalp.bannerSrc}
          alt={`${kalp.name} banner`}
          className="w-full h-64 object-cover"
          onError={(e) => {
            // Fallback gradient if image not yet loaded
            e.target.style.display = "none";
            e.target.parentNode.classList.add(
              `bg-gradient-to-br`, kalp.gradFrom, kalp.gradTo,
              "flex", "items-center", "justify-center", "h-64"
            );
          }}
        />
      </div>

      {/* Collection label */}
      <div className="flex justify-center mb-6">
        <span className={`px-6 py-2 rounded-full text-sm font-semibold shadow ${kalp.collectionColor}`}>
          {kalp.collectionLabel}
        </span>
      </div>

      {/* Shop CTA */}
      <div className="text-center">
        <a
          href="/shop"
          className="inline-block px-8 py-3 rounded-full bg-gradient-to-r from-orange-400 to-pink-500 text-white font-semibold shadow-lg hover:scale-105 transition"
        >
          Shop {kalp.name} →
        </a>
      </div>

      {/* COMMUNITY SECTION */}
      <div className="mt-16">

        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900">
            {kalp.name} Community
          </h2>

          <p className="text-gray-600 mt-2">
            Connect with people who share the same mindset.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">

          <div className="bg-white/60 rounded-2xl p-4 text-center">
            <div className="text-2xl font-bold">12k</div>
            <div className="text-xs text-gray-500">Members</div>
          </div>

          <div className="bg-white/60 rounded-2xl p-4 text-center">
            <div className="text-2xl font-bold">4k</div>
            <div className="text-xs text-gray-500">Posts</div>
          </div>

          <div className="bg-white/60 rounded-2xl p-4 text-center">
            <div className="text-2xl font-bold">800</div>
            <div className="text-xs text-gray-500">Online</div>
          </div>

        </div>

        <FeedTab
          category={categoryMap[kalp.id]}
          theme={kalp.bgGlow}
        />
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   EXPLORE KALPS GRID
───────────────────────────────────────────────────────────────────────────── */
function ExploreGrid({ onSelectKalp }) {
  const navigate = useNavigate();
  return (
    <section className="max-w-6xl mx-auto mt-24 px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-10"
      >
        <h2 className="text-3xl font-bold text-gray-800">Explore Kalps</h2>
        <p className="text-gray-600 mt-2">Five ways people move through life. Find yours.</p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {KALPS.map((kalp, i) => (
          <motion.div
            key={kalp.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
          >
            <GlassTile className="!p-0 overflow-hidden flex flex-col h-full cursor-pointer group">
              {/* Kalp image */}
              <div className="relative w-full aspect-[3/4] overflow-hidden rounded-t-2xl bg-gray-100">
                <img
                  src={kalp.cardSrc}
                  loading="lazy"
                  srcSet={`
                        ${kalp.cardSrc}?w=200 200w,
                        ${kalp.cardSrc}?w=400 400w,
                        ${kalp.cardSrc}?w=800 800w
                      `}
                  sizes="(max-width: 640px) 200px,
         (max-width: 1024px) 400px,
         800px"
                  alt={kalp.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.parentNode.style.background = `linear-gradient(135deg, ${kalp.accent}33, ${kalp.accent}88)`;
                  }}
                />
              </div>

              {/* Card text + button */}
              <div className="p-5 flex flex-col flex-1 justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-1">{kalp.name}</h3>
                  <p className="text-sm text-gray-600 leading-snug">{kalp.tagline}</p>
                </div>
                <button
                  onClick={() => onSelectKalp(kalp)}
                  className="mt-4 w-full py-2 rounded-full bg-blue-600 hover:bg-blue-700 active:scale-95 text-white text-sm font-semibold shadow transition-all"
                >
                  Explore
                </button>
              </div>
            </GlassTile>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN LANDING PAGE
───────────────────────────────────────────────────────────────────────────── */
export default function LandingPage() {
  const navigate = useNavigate();
  const [activeKalp, setActiveKalp] = useState(null);

  const handleShopNow = () => navigate("/shop");

  return (
    <div className="min-h-screen overflow-y-auto" style={{ overflowX: "hidden" }}>


      <AnimatePresence mode="wait">
        {activeKalp ? (
          /* ── KALP DETAIL VIEW ───────────────────────────────────── */
          <motion.div
            key="detail"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="pt-20"
          >
            <KalpDetailPage
              kalp={activeKalp}
              onBack={() => setActiveKalp(null)}
            />
          </motion.div>
        ) : (
          /* ── MAIN LANDING VIEW ──────────────────────────────────── */
          <motion.div
            key="landing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* ── HERO ── */}
            <section className="pt-20 text-center px-6">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-3xl mx-auto"
              >
                <GlassTile>
                  <h1 className="text-5xl font-bold mb-4">Build Become Belong.</h1>
                  <div className="space-y-2 text-lg text-gray-700 mb-6">
                    <p>You already know who you are.
                      You just haven't stepped into it yet.</p>
                    <p>Think, speak, and build through your Kalp.</p>
                    <p>Shape the narrative you choose to live.</p>
                  </div>
                  <div className="flex justify-center">
                    <button
                      onClick={() => navigate("/community/DREAM_KALP")}
                      className="px-6 py-3 rounded-full bg-gradient-to-r from-orange-400 to-pink-500 text-white font-semibold shadow-lg hover:scale-105 transition"
                    >
                      Enter the Kalp Universe
                    </button>
                  </div>
                </GlassTile>
              </motion.div>
            </section>

            {/* ── OUR STORY ── */}
            <section className="max-w-6xl mx-auto mt-24 px-6">
              <GlassTile className="flex flex-col md:flex-row items-center gap-10 !p-8">
                <div className="flex-1">
                  <h2 className="text-3xl font-bold mb-4">Our Story</h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    <span className="font-bold">5Buddies</span> was created with a simple belief —
                    people are more than just users or customers. Everyone carries a unique way of
                    thinking, creating, and living. At <span className="font-bold">5Buddies</span>,
                    these identities are called <span className="font-bold">Kalps</span>.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <span className="font-bold">CreatiStyle</span> is the first step of this
                    ecosystem, where each design represents a Kalp and helps people express who they
                    are. Over time, 5Buddies will grow into a larger world of communities, tools,
                    and spaces where people can connect, create, and truly belong.
                  </p>
                </div>

                {/* Story hero image — the orange Kalp + hoodie figure */}
                <div className="flex-1 w-full">
                  <div className="w-full aspect-[4/3] md:aspect-[16/10] rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src={heroStory}
                      alt="5Buddies — BUILD. BECOME. BELONG."
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.parentNode.className +=
                          " bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center";
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                </div>
              </GlassTile>
            </section>

            {/* ── EXPLORE KALPS GRID ── */}
            <ExploreGrid onSelectKalp={(kalp) => navigate(`/community/${kalp.id}`)} />

            {/* ── COMMUNITY ── */}
            <section className="max-w-6xl mx-auto mt-24 px-6 text-center">
              <GlassTile>
                <h2 className="text-3xl font-bold mb-3">Step into your Kalp</h2>
                <p className="text-gray-700 mb-6">
                  You are the Nomad, moving through different identities. Milo is your buddy, helping you express them.
                  Explore the Kalps. Find where you are, and where you're going.
                </p>
                <a
                  href="/community/dream"
                  className="px-6 py-3 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 text-white font-semibold shadow hover:scale-105 transition"
                >
                  Enter your Kalp universe
                </a>
              </GlassTile>
            </section>

            {/* ── CTA ── */}
            <section className="max-w-4xl mx-auto mt-24 px-6 text-center">
              <GlassTile>
                <h2 className="text-3xl font-bold mb-4">Start expressing your Kalp</h2>
                <p className="text-gray-700 mb-6">
                  Each piece you wear reflects a part of who you are becoming.
                  Choose your Kalp and bring it into the real world.
                </p>
                <a
                  href="/shop"
                  className="px-8 py-3 rounded-full bg-gradient-to-r from-orange-400 to-pink-500 text-white font-semibold shadow-lg hover:scale-105 transition"
                >
                  Explore CreatiStyle
                </a>
              </GlassTile>
            </section>


            {/* ── FOOTER ── */}
            <footer className="mt-24 mb-6 px-6">
              <GlassTile className="text-center text-gray-700 text-sm py-4">
                © 2025 5Buddies — Designed for creators, dreamers & rebels.
                <div className="mt-2 space-x-4">
                  <a href="#" className="hover:text-blue-700">Privacy</a>
                  <a href="#" className="hover:text-blue-700">Terms</a>
                  <a href="#" className="hover:text-blue-700">Contact</a>
                </div>
              </GlassTile>
            </footer>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}